import FloatingVue from 'floating-vue';
import { createPinia } from 'pinia';
import { authMiddleware, pageTitleMiddleware, routes, scrollBehavior } from '@/router/router';
import * as Sentry from '@sentry/vue';
import { ViteSSG } from 'vite-ssg';
import VueToastification from 'vue-toastification';

import App from './App.vue';

import config from '@/config';
import 'floating-vue/dist/style.css';
import 'vue-toastification/dist/index.css';
import './style.css';

export let routerInstance = null; // Export a variable to hold the router

const vFocus = {
  mounted: el => el.focus(),
};

// Use ViteSSG to create the app
export const createApp = ViteSSG(
  // Root component
  App,
  // Vue Router options with your routes instance { routes, history, scrollBehavior, etc. }
  // Pass the whole router configuration object used by createRouter
  { routes: routes, base: import.meta.env.BASE_URL, scrollBehavior: scrollBehavior },
  // Function to configure the app instance (plugins, directives)
  async ({ app, router: appRouter }) => { // Receive router and head instance
    // --- Store the router instance ---
    routerInstance = appRouter; // Assign the created router to the exported variable

    const pinia = createPinia();
    app.use(pinia);
    app.use(FloatingVue);

    // Add Sentry integration for Vue Router after it's initialized
    if (config.sentryDsn) {

      // Only initialise Sentry if the DSN has been set, which is generally only in Staging or Production
      Sentry.init({
        app,
        dsn: config.sentryDsn,
        sendDefaultPii: true,
        environment: config.env,
        integrations: [
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
          Sentry.browserTracingIntegration({ router: appRouter }),
        ],
        replaysSessionSampleRate: config.env === 'production' ? 0.1 : 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

      });
      Sentry.attachErrorHandler(app, {
        logErrors: config.env === 'development', // Log errors in dev
      });
    }

    // Register directives
    app.directive('focus', vFocus);

    // Use `typeof window` for browser-only APIs
    if (typeof window !== 'undefined') {

      // Conditionally import and initialize Clarity
      if (config.clarityProjectId) {
        try {
          // Use dynamic import INSIDE the isClient check
          const Clarity = (await import('@microsoft/clarity')).default; // Assuming default export
          Clarity.init(config.clarityProjectId);
          console.log(`[Clarity] Initialized for ${config.env}.`);
        } catch (error) {
          console.error('Failed to load or initialize Clarity:', error);
        }
      }

      // 4. Dynamically load and configure Google Analytics
      if (config.gaId) {
        const gaScript = document.createElement('script');
        gaScript.async = true;
        gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${config.gaId}`;
        document.head.appendChild(gaScript);

        window.dataLayer = window.dataLayer || [];

        function gtag() {
          dataLayer.push(arguments);
        }

        gtag('js', new Date());
        gtag('config', config.gaId);
        console.log(`[GA] Initialized for ${config.env}.`);
      }

      appRouter.beforeEach(authMiddleware);
      appRouter.afterEach(pageTitleMiddleware);
      console.log('[Middleware] Client-side router middleware applied.');

      const toastOptions = {
        timeout: 4000,
        position: 'bottom-right', // Use string literal
        transition: 'Vue-Toastification__fade',
        // Add any other global toast options here
      };
      app.use(VueToastification, toastOptions); // <-- INSTALL PLUGIN HERE
      console.log('[VueToastification] Plugin installed.');
    }
  },
);
